import { FormRenderProps } from "@progress/kendo-react-form";
import * as React from "react";

type ReturnType = [
  FormRenderProps | undefined,
  (value: FormRenderProps) => void,
];

/**
 * Captures Kendo Form's render props for use outside the Form render callback.
 *
 * We intentionally avoid deep equality (lodash isEqual) here because
 * FormRenderProps contains function references (onSubmit, onChange, valueGetter,
 * etc.) that Kendo recreates on every render. Using isEqual on those always
 * returns false, causing setState to fire every render, which triggers the
 * parent to re-render, which re-triggers Kendo's internal useEffect, creating
 * an infinite "Maximum update depth exceeded" loop.
 *
 * Instead we compare only the primitive boolean values that actually affect
 * the parent's JSX (valid, modified, touched) plus the onSubmit reference.
 * This lets React bail out of re-rendering when nothing meaningful has changed,
 * while still re-rendering when the form's interactive state genuinely changes.
 */
function useFormPropRef(): ReturnType {
  const [ref, setRef] = React.useState<FormRenderProps>();
  const setFormRef = React.useCallback((value: FormRenderProps) => {
    setRef(currentValue => {
      if (!currentValue) return value;
      const unchanged =
        currentValue.valid === value.valid &&
        currentValue.modified === value.modified &&
        currentValue.touched === value.touched &&
        currentValue.onSubmit === value.onSubmit;
      return unchanged ? currentValue : value;
    });
  }, []);
  return [ref, setFormRef];
}

export default useFormPropRef;
